<template>
  <div class="row px-3 pt-2 pb-0 main-container">
    <div class="col folder-container" style="max-width: 216px !important;">
      <base-search
        v-model="imageSearch"
        class="mb-2"
        icon-position="left"
      />
      <base-list
        :page-size="10"
        :items="folders"
        :count="count"
        :loading="loading"
        mode="loadMore"
        :load-more-bottom-threshold="10"
        :show-load-more="false"
        class="folders pt-1"
        @load-more="loadMore"
        @reached-bottom="loadMore"
      >
        <template #loading>
          <base-skeleton
            v-for="n in 4"
            :key="n"
            :opacity="0 + (1 /n)"
            class="mb-0"
          >
            <div class="d-flex align-items-center">
              <div
                class="skeleton__item mx-1 my-0"
                style="max-width: 200px"
              />
            </div>
          </base-skeleton>
        </template>
        <template #default="{item}">
          <base-button
            class="custom-folder"
            :secondary="selectedFolderPk === item.pk && !imageSearch.length"
            :class="`m-0 py-1 px-3 text-left base-button ${selectedFolderPk === item.pk ? 'selected-folder':''}`"
            borderless
            nowrap
            @click="selectedFolderPk = item.pk; imageSearch = ''; $refs.collateralItems.scrollTop = 0"
          >
            <!-- isSynced is related is looking for the flag sync_target:"WEBSITE" on each folder  -->
            {{ isSynced(item) ? "* " : '' }}{{ item.name ? item.name : `${$t('outreach.product.untitled')} ${item.pk} ` }}
          </base-button>
        </template>
        <template #nodata>
          <empty
            min-height="30px"
            title="No results"
          />
        </template>
      </base-list>
    </div>
    <div
      ref="collateralItems"
      v-scrollable="{
        heightThresholdAdjust: 20
      }"
      class="col collateral-selector mt-n2"
      style="padding-left: 24px; padding-top: 5px;"
      v-on="$listeners"
      @reached-bottom="onLoadMoreImages"
    >
      <div
        class="container pt-1"
        style="padding-right: unset; padding-left: unset;"

      >
        <div class="collateral-items">
          <div
            v-for="(item, index) in images"
            :key="`collateral_${index}`"
            style="border-radius: 4px;"
            @click="onClick(item)"
          >
            <collateral-item
              :selected="isSelected(item)"
              :image="imageURL(item) || ''"
            />
            <!-- <base-text
              v-if="getItemTitle(item).length > 0"
              size="xs"
              class="mt-2 text-center"
            >
              {{ getItemTitle(item) }}
            </base-text> -->
          </div>

          <!-- Skeleton moved inside the list to emulate Items and prevent a jump when scrolling -->
          <template v-if="imagesLoading && images.length === 0">
            <base-skeleton
              v-for="n in MIN_SKELETON_SLOT_COUNT"
              :key="`loadingCollateral-${n}`"
              no-background
              class="mb-0"
            >
              <div class="">
                <div
                  class="skeleton__item mt-0"
                  style="width: 100%; height: 120px;"
                  :style="{opacity: 0 + (1 /n)}"
                />
              </div>
            </base-skeleton>
          </template>
        </div>
      </div>
    </div>
    <base-text
      size="s"
      class="mt-3 mb-n2"
    />
  </div>
</template>
<script>
import { SYNC_TARGETS } from '@/_utilities/Enums';
import { getCollateralImageURL } from '@/_utilities/generalUseUtils';
import Listable, { PAGINATION_MODE_LOAD_MORE } from '@/components/mixins/listable';

export default {
  name: 'CollateralSelector',
  mixins: [Listable],
  props: {
    // v-model selected images
    value: {
      type: Array,
      default: () => ([]),
    },
    images: {
      type: Array,
      default() {
        return [];
      },
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    showSharedCollages: {
      type: Boolean,
      default: true,
    },

    // -- listable.js props --
    mode: {
      type: String,
      default: PAGINATION_MODE_LOAD_MORE, // 'loadMore'
    },
    pageSize: {
      type: Number,
      default: 30,
    },
    source: {
      type: String,
      default: 'folders',
    },

  },
  data() {
    return {
      MIN_SKELETON_SLOT_COUNT: 4,
      imageSearch: '',
      selected: [],
      selectedFolderPk: -1,
      // page: 1,

      // -- listable.js data --
      queryParams: {
        fromCollateralSelector: true,
      },
    };
  },
  computed: {
    imagesLoading() {
      return this.$store.state.collateral.isLoading;
    },
    count() {
      return this.$store.state.folders.count;
    },
    folders() {
      const allFolder = {
        pk: -1,
        files_count: 0,
        is_active: true,
        name: 'View All',
        all: true,
      };
      const sharedFolder = {
        pk: -2,
        files_count: 0,
        is_active: true,
        name: 'Shared Collages',
        shared: true,
      };

      if (!this.elements) {
        return [allFolder];
      }

      return [allFolder,
        ...(this.showSharedCollages && this.useFeature('display_shared_collages')
          ? [sharedFolder] : []),
        ...this.elements,
      ];
    },
    selectedFolder() {
      return this.folders.find((f) => f.pk === this.selectedFolderPk);
    },
  },
  watch: {
    selected() {
      const modifiedSelected = this.selected.map((x) => {
        if (x.file) {
          x.site_url = x.file;
        }
        x.image = this.imageURL(x);
        return x;
      });
      this.$emit('input', modifiedSelected);
    },
    imageSearch(newVal) {
      this.$emit('search', { search: newVal, folder: this.selectedFolder });
    },
    selectedFolderPk: {
      immediate: true,
      handler(newval) {
        this.imageSearch = '';
        this.$emit('folder-changed', this.selectedFolder);
      },
    },
  },
  methods: {
    imageURL(item) {
      return getCollateralImageURL(item);
    },
    isSynced(folder) {
      return folder.sync_target === SYNC_TARGETS.WEBSITE;
    },
    getItemTitle(item) {
      // None if folder is not synced
      if (item.sync_target !== SYNC_TARGETS.WEBSITE) return '';
      try {
        const jsonmeta = JSON.parse(item.meta);
        return jsonmeta?.title ?? '';
      } catch (error) {
        return '';
      }
    },
    onClick(item) {
      if (this.isSelected(item)) {
        this.removeItem(item);
        return;
      }
      const localItem = item;
      if (localItem.image_url == null) {
        localItem.image_url = localItem.image;
      }
      if (!this.multiple) {
        this.selected = [];
      }
      this.selected.push(localItem);
    },
    removeItem(item) {
      this.selected = this.selected.filter((i) => i.pk !== item.pk);
    },
    isSelected(item) {
      return this.selected.filter((i) => i.pk === item.pk).length > 0;
    },
    onLoadMoreImages() {
      this.$emit('load-more-images');
    },
  },
};
</script>
<style lang="scss" scoped>
.collateral-selector {
  display: flex;
  flex-wrap: wrap;
  max-height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  align-items: flex-start;
  -webkit-overflow-scrolling: touch;
}

.collateral-item {
  background-color: $gray-light;
  cursor: pointer;
}

.selected-folder {
  font-family: 'SilkaMedium' !important;
  border-radius: 4px !important;
  background-color: #F0F0F0 !important;
  color: #1B1B1B !important;
}
.custom-folder{
  font-size: 14px;
  color: #767676;
  font-family: 'SilkaLight';
  width: 100% !important;
  height: 35px;
  padding-top: unset !important;
  padding-bottom: unset !important;

}
.main-container {
  @include nested-scrollbars;
  // height: 60vh;
  height: 515px;
  max-height: 880px; // Forces folders to always be scrollable
  padding-top: 0;
  margin-top: -21px;
}

.folder-container {
  max-height: 100%;
  padding-right: 0;

  .folders {
    --top: 50px;
    max-height: calc(100% - var(--top));
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.collateral-items {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 1fr;
  align-items: start;
  justify-items: stretch;
  gap: 12px 12px;

  @media (max-width: 800px){
    grid-template-columns: repeat(2, 1fr) !important;
  }
  @media only screen and (max-width: 925px) and (min-width: 801px) {
    grid-template-columns: repeat(3, 1fr) !important;
  }
}
::v-deep .collateral-item .thumbnail img{
  height: 180px;
}
::v-deep .base-search {
    width: 100% !important;
    min-width: auto;
    margin-right: 0;
    &__input {
      // font
      font-family: 'SilkaLight' !important;
      // design
      background-color: #FFF;
      border: 1px solid #C6C6C6;
      border-radius: 5px;
      font-family: $font-family-sans-serif-medium;
      max-width: 100%;
      max-height:36px;
    }
    ::placeholder {
      font-size: 14px;
      color: #909090;
    }
  }
  ::v-deep .base-search__icon--left {
  margin-left: 5px;
}
::v-deep .base-search {
    width: 100% !important;
    min-width: auto;
    margin-right: 0;
    &__input {
      // font
      font-family: 'SilkaLight' !important;
      // design
      background-color: #FFF;
      border: 1px solid #C6C6C6;
      border-radius: 5px;
      font-family: $font-family-sans-serif-medium;
      max-width: 100%;
      max-height:36px;
    }
    ::placeholder {
      font-size: 14px;
      color: #909090;
    }
  }
  ::v-deep .base-search__icon--left {
  margin-left: 5px;
}
</style>
